import React from 'react';
import { View, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { FontAwesome } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import Style from '../../../../../constants/Style';
import Colors from '../../../../../constants/Colors';
import { AppScreen } from '../../../../../navigation/LinkingConfiguration';
import { UserPermission } from '../../../../../constants/Types';
import { Area, Space } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import PressableStyled from '../../../../../components/PressableStyled';
import EditIcon24 from '../../../../../components/24pxIcons/EditIcon24';
import LocationIcon from '../../../../../components/30pxIcons/LocationIcon';
import { updateBuildingId } from '../../../../../redux/selectedBuildingSlice';

type Section = {
  title: string,
  count: string | number,
}

const SectionItem = ({ title, count }: Section) => <View style={styles.section}>
  <Text style={styles.sectionTitle}>{title}</Text>
  <Text style={styles.sectionCount}>{count}</Text>
</View>;

interface AreaTitleItemProps extends Area {
  edit: () => void,
  clear: () => void,
}

const AreaTitleItem = ({
  Name, spaces, assetCount, sensorCount, meterCount, edit, ParentId: floorId, LocationId: areaId,
  clear, buildingId,
}: AreaTitleItemProps) => {
  const dispatch = useDispatch();
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);

  const navigation = useNavigation();
  let assetCountIncludingSublocations: number = assetCount ?? 0;
  let sensorCountIncludingSublocations: number = sensorCount ?? 0;
  let meterCountIncludingSublocations: number = meterCount ?? 0;
  let spaceCount: number = 0;
  Object.values(spaces ?? {}).forEach((space: Space) => {
    assetCountIncludingSublocations += space.assetCount ?? 0;
    sensorCountIncludingSublocations += space.sensorCount ?? 0;
    meterCountIncludingSublocations += space.meterCount ?? 0;
    spaceCount += 1;
  });

  const navigateToMap: () => void = () => {
    dispatch(updateBuildingId(buildingId));
    navigation.navigate(AppScreen.MAP as any, { floorId, areaId });
  };

  return <View style={[Style.row, styles.container]}>
    <View style={styles.nameSection}>
      <PressableStyled
        onPress={clear}
        style={styles.icon}
      >
        <FontAwesome
          name='angle-left'
          size={24}
          color={Colors.light.hereworksBlue500}
        />
      </PressableStyled>
      <Text style={styles.name}>{Name}</Text>
      {canUpdateLocations && <PressableStyled
        onPress={edit}
        style={styles.icon}
      >
        <EditIcon24 />
      </PressableStyled>}
      <PressableStyled
        onPress={navigateToMap}
        style={styles.icon}
      >
        <LocationIcon color={Colors.light.hereworksBlue500} />
      </PressableStyled>
    </View>
    <View style={[Style.row, { flex: 1 }]}>
      <SectionItem
        title='SPACES'
        count={spaceCount}
      />
      <SectionItem
        title='ASSETS'
        count={assetCountIncludingSublocations}
      />
      {happyEnabled && <SectionItem
        title='SENSORS & METERS'
        count={sensorCountIncludingSublocations + meterCountIncludingSublocations}
      />}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    marginBottom: 40,
    marginHorizontal: 24,
  },
  nameSection: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontSize: 18,
    lineHeight: 28,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginHorizontal: 8,
  },
  icon: {
    marginEnd: 8,
  },
  section: {
    marginStart: 32,
  },
  sectionTitle: {
    fontSize: 11,
    lineHeight: 13,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  sectionCount: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_500Medium',
    color: Colors.light.hereworksBlack600,
  },
});

export default AreaTitleItem;
